import { useCurrencyStore } from '@/pinia/currency'
import { useCoupon } from './useCoupon'
import { useCart } from './useCart'
import { createSharedComposable } from '@vueuse/core'
import { bankersRoundWithMax, debounce } from '@/utils/utils'
import { useAddressStore } from '@/pinia/address'

export const useOrder = createSharedComposable(() => {
  const { coupon } = useCoupon()
  const currency = useCurrencyStore()
  const addressStore = useAddressStore()

  const { loading, cartList, calcShipFee, isProcessing } = useCart()

  const shipPackage = ref(null)

  const paymentType = ref(0) // 0 paypal, 1 bank

  const selectedSkuList = computed(() => {
    const list = []
    cartList.value.forEach(spu => {
      spu.skuDetails.forEach(sku => {
        if (sku.checked) {
          /**
           * 为 计算履约日志 服务提前设置需要的数据
           */
          sku.spuName = spu.productName
          list.push(sku)
        }
      })
    })
    return list
  })

  const selectedSkuCount = computed(() => {
    return selectedSkuList.value.reduce((pre, cur) => {
      return pre + cur.quantity
    }, 0)
  })

  const deliveryTime = computed(() => {
    return { min: shipPackage.value?.minDeliveryTime ?? 7, max: shipPackage.value?.maxDeliveryTime ?? 15 }
  })

  const hasProcessingSelected = computed(() => {
    return cartList.value.some(item => item.skuDetails.some(sku => sku.checked && isProcessing(sku.cartStatus)))
  })

  const hasUndeliverableSelected = computed(() => {
    return cartList.value.some(item => item.skuDetails.some(sku => sku.checked && sku.undeliverable))
  })

  const totalItemsPrice = computed(() => {
    let total = 0
    selectedSkuList.value.forEach(item => {
      const targetPrice = bankersRoundWithMax(item.price * currency.current.rate, 0.01)
      total += targetPrice * item.quantity
    })
    return total.toFixed(2)
  })

  // 合并物流价
  const shippingCost = computed(() => {
    if (!shipPackage.value) return 0
    return shipPackage.value.shipAmount
  })

  // 拆分物流价
  const splitShippingCost = computed(() => {
    let total = 0
    cartList.value.forEach(cart => {
      const hasSkuChecked = cart.skuDetails.some(sku => sku.checked)
      if (hasSkuChecked) {
        total += Number(cart.shipAmount)
      }
    })
    return total.toFixed(2)
  })

  const totalAmount = computed(() => {
    const v = Number(totalItemsPrice.value) + Number(shippingCost.value)
    return v.toFixed(2)
  })

  const totalItemsPriceUSD = computed(() => {
    return selectedSkuList.value.reduce((pre, cur) => {
      pre += bankersRoundWithMax(currency.exchangeCurrency(cur.price, 'CNY', 'USD'), 0.01) * cur.quantity
      return pre
    }, 0).toFixed(2)
  })

  const discountAmountUSD = computed(() => {
    if (coupon.value == null) return 0
    if (Number(totalItemsPriceUSD.value) < Number(coupon.value.minOrderAmount)) return 0
    return coupon.value.discountAmount
  })

  const totalAmountAfterDiscount = computed(() => {
    if (selectedSkuList.value.length === 0) return '0.00'
    const discountAmount = discountAmountUSD.value || 0

    const shippingCostUSD = shipPackage.value ? shipPackage.value.shipAmountUsd : 0

    return (Number(totalItemsPriceUSD.value) + Number(shippingCostUSD) - Number(discountAmount)).toFixed(2)
  })

  /**
   * 用于判断上一次支付方式是否是 自动切换支付方式 触发的，如果是则返回到默认支付方式
   */
  let isAutoChangedPayment = false

  /**
   * 当订单金额超过 500 USD 时，只允许使用银行转账
   */
  watch(totalAmountAfterDiscount, (value) => {
    if (value > 500 && paymentType.value !== 1) {
      isAutoChangedPayment = true
      paymentType.value = 1
    } else if (value <= 500 && isAutoChangedPayment) {
      isAutoChangedPayment = false
      paymentType.value = 0
    }
  })

  /**
   * 防止请求竞态导致列表数据展示不正确问题
   */
  let latestRequestId = 0

  const calcSelectSkuShipFee = async () => {
    const currentRequestId = ++latestRequestId
    if (selectedSkuList.value.length > 0) {
      const result = await calcShipFee(selectedSkuList.value)
      if (currentRequestId === latestRequestId) {
        shipPackage.value = result
      }
    }
  }

  watch(() => [selectedSkuList.value, selectedSkuCount.value], debounce(() => {
    calcSelectSkuShipFee()
  }, 500))

  /**
   * 地址修改后需要重新请求物流费
   */
  watch(() => addressStore.cacheAddress, async () => {
    loading.value = true
    await calcSelectSkuShipFee()
    loading.value = false
  })

  /**
   * 监听到用户货币切换后重新请求物流费
   */
  watch(() => currency.current.targetCurrency, async () => {
    loading.value = true
    await calcSelectSkuShipFee()
    loading.value = false
  })

  return {
    paymentType,
    deliveryTime,
    selectedSkuList,
    selectedSkuCount,
    hasProcessingSelected,
    hasUndeliverableSelected,
    discountAmountUSD,
    totalItemsPrice,
    shippingCost,
    splitShippingCost,
    totalAmount,
    totalAmountAfterDiscount
  }
})
